import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { useCurrency } from '@/contexts/CurrencyContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { CheckCircle, AlertCircle, Clock, Save, XCircle, Home, Trash2, Inbox, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { colorOptions, getRandomColor } from '@/utils/colors';
import { API_BASE_URL } from '@/lib/apiBaseUrl';

interface Category {
  id: string;
  name: string;
  color: string;
  type: 'expense' | 'income';
}

interface Transaction {
  id: string;
  date: string;
  amount: number;
  description: string;
  businessName?: string;
  paymentType?: string;
  cardNumber?: string;
  categoryId: string;
  category: Category | null;
}

interface ReviewSession {
  sessionValid: boolean;
  expiresAt: string;
  transactions: Transaction[];
  categories: Category[];
}

export default function TransactionReview() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { formatAmount } = useCurrency();
  const { language } = useLanguage();
  const { t } = useTranslation();
  const isRTL = language === 'he';

  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState<ReviewSession | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [errorType, setErrorType] = useState<'not_found' | 'expired' | 'network' | 'unknown'>('unknown');
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [hasSaved, setHasSaved] = useState(false);
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());
  const [createCategoryOpen, setCreateCategoryOpen] = useState(false);
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [categoryForTransactionId, setCategoryForTransactionId] = useState<string | null>(null);
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    color: getRandomColor(),
    type: 'expense' as 'expense' | 'income',
  });

  const fetchReviewSession = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${API_BASE_URL}/transaction-review/${token}`,
      );

      if (!response.ok) {
        if (response.status === 404) {
          setError(isRTL ? 'קישור לא נמצא במערכת' : 'Review session not found');
          setErrorType('not_found');
        } else if (response.status === 400) {
          const data = await response.json();
          const message = data.message || '';
          if (message.includes('expired')) {
            setError(isRTL ? 'תוקף הקישור פג (2 שעות)' : 'Review session has expired');
            setErrorType('expired');
          } else {
            setError(isRTL ? 'קישור לא תקין' : 'Invalid review session');
            setErrorType('unknown');
          }
        } else {
          setError(isRTL ? 'שגיאה בטעינת הנתונים' : 'Failed to load review session');
          setErrorType('network');
        }
        return;
      }

      const data: ReviewSession = await response.json();
      setSession(data);
      setTransactions(data.transactions);
    } catch (err) {
      console.error('Error fetching review session:', err);
      setError(
        isRTL
          ? 'שגיאה בחיבור לשרת. אנא בדוק את החיבור לאינטרנט.'
          : 'Network error. Please check your internet connection.',
      );
      setErrorType('network');
    } finally {
      setLoading(false);
    }
  }, [token, isRTL]);

  useEffect(() => {
    if (token) {
      fetchReviewSession();
    }
  }, [token, fetchReviewSession]);

  const handleTransactionUpdate = (
    transactionId: string,
    field: string,
    value: string | number,
  ) => {
    setTransactions((prev) =>
      prev.map((tx) =>
        tx.id === transactionId
          ? {
              ...tx,
              [field]: value,
              ...(field === 'categoryId' && {
                category:
                  session?.categories.find((cat) => cat.id === value) || null,
              }),
            }
          : tx,
      ),
    );
    setHasChanges(true);
  };

  const handleSaveAll = async () => {
    if (!token || !hasChanges) return;

    try {
      setSaving(true);
      for (const transaction of transactions) {
        await fetch(
          `${API_BASE_URL}/transaction-review/${token}`,
          {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              transactionId: transaction.id,
              date: transaction.date,
              amount: typeof transaction.amount === 'string' 
                ? parseFloat(transaction.amount) 
                : transaction.amount,
              categoryId: transaction.categoryId,
              description: transaction.description,
              businessName: transaction.businessName,
              paymentType: transaction.paymentType,
              cardNumber: transaction.cardNumber,
            }),
          },
        );
      }

      toast.success(
        isRTL
          ? 'העסקאות עודכנו בהצלחה! ✓'
          : 'Transactions updated successfully! ✓',
        {
          description: isRTL
            ? 'הנתונים נשמרו במערכת'
            : 'All changes have been saved',
        },
      );
      setHasChanges(false);
      setHasSaved(true);
    } catch (err) {
      console.error('Error saving transactions:', err);
      toast.error(
        isRTL ? 'שגיאה בשמירת העסקאות' : 'Error saving transactions',
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTransaction = async (transactionId: string) => {
    if (!token) return;

    // Confirm deletion
    const confirmed = window.confirm(
      isRTL
        ? 'האם אתה בטוח שברצונך למחוק עסקה זו?'
        : 'Are you sure you want to delete this transaction?'
    );

    if (!confirmed) return;

    try {
      setDeletingIds((prev) => new Set(prev).add(transactionId));

      const response = await fetch(
        `${API_BASE_URL}/transaction-review/${token}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            transactionId,
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to delete transaction');
      }

      // Remove transaction from local state
      setTransactions((prev) =>
        prev.filter((t) => t.id !== transactionId)
      );

      toast.success(
        isRTL ? 'העסקה נמחקה בהצלחה' : 'Transaction deleted successfully'
      );

      // If no transactions left, show a special message
      if (transactions.length === 1) {
        toast.success(
          isRTL
            ? 'כל העסקאות נמחקו! 🎉'
            : 'All transactions deleted! 🎉',
          {
            description: isRTL
              ? 'אין עסקאות נוספות לסקירה'
              : 'No more transactions to review'
          }
        );
      }
    } catch (err) {
      console.error('Error deleting transaction:', err);
      toast.error(
        isRTL
          ? 'שגיאה במחיקת העסקה'
          : 'Error deleting transaction'
      );
    } finally {
      setDeletingIds((prev) => {
        const next = new Set(prev);
        next.delete(transactionId);
        return next;
      });
    }
  };

  const handleCreateCategory = async () => {
    if (!token || !categoryForm.name.trim()) {
      toast.error(isRTL ? 'נא להזין שם קטגוריה' : 'Please enter a category name');
      return;
    }

    try {
      setCreatingCategory(true);

      const response = await fetch(
        `${API_BASE_URL}/transaction-review/${token}/category`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(categoryForm),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to create category');
      }

      const newCategory: Category = await response.json();

      // Add the new category to the session's categories
      if (session) {
        setSession({
          ...session,
          categories: [...session.categories, newCategory],
        });
      }

      // Set the new category on the transaction that triggered the dialog
      if (categoryForTransactionId) {
        handleTransactionUpdate(categoryForTransactionId, 'categoryId', newCategory.id);
      }

      toast.success(
        isRTL ? 'הקטגוריה נוצרה בהצלחה!' : 'Category created successfully!'
      );

      // Reset form and close dialog
      setCategoryForm({ name: '', color: getRandomColor(), type: 'expense' });
      setCategoryForTransactionId(null);
      setCreateCategoryOpen(false);
    } catch (err) {
      console.error('Error creating category:', err);
      toast.error(
        isRTL ? 'שגיאה ביצירת קטגוריה' : 'Error creating category'
      );
    } finally {
      setCreatingCategory(false);
    }
  };

  if (loading) {
    return (
      <div
        className={`min-h-screen flex items-center justify-center p-4 ${isRTL ? 'rtl' : 'ltr'}`}
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        <Card className="max-w-md w-full p-8 text-center">
          <div className="flex flex-col items-center space-y-4">
            <div className="animate-spin">
              <Clock className="h-12 w-12 text-blue-500" />
            </div>
            <h2 className="text-xl font-semibold">
              {isRTL ? 'טוען נתונים...' : 'Loading...'}
            </h2>
            <div className="w-full space-y-3">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4 mx-auto" />
              <Skeleton className="h-4 w-5/6 mx-auto" />
            </div>
          </div>
        </Card>
      </div>
    );
  }

  if (error || (!session && !loading)) {
    const getErrorIcon = () => {
      switch (errorType) {
        case 'expired':
          return <Clock className="h-16 w-16 md:h-20 md:w-20 text-orange-500" />;
        case 'not_found':
          return <AlertCircle className="h-16 w-16 md:h-20 md:w-20 text-red-500" />;
        case 'network':
          return <XCircle className="h-16 w-16 md:h-20 md:w-20 text-yellow-500" />;
        default:
          return <AlertCircle className="h-16 w-16 md:h-20 md:w-20 text-gray-500" />;
      }
    };

    const getErrorTitle = () => {
      switch (errorType) {
        case 'expired':
          return isRTL ? 'תוקף הקישור פג' : 'Link Expired';
        case 'not_found':
          return isRTL ? 'קישור לא נמצא' : 'Link Not Found';
        case 'network':
          return isRTL ? 'שגיאת חיבור' : 'Connection Error';
        default:
          return isRTL ? 'שגיאה' : 'Error';
      }
    };

    const getErrorMessage = () => {
      if (error) return error;
      return isRTL ? 'לא נמצאה הפעלת סקירה' : 'Review session not found';
    };

    const getErrorHelp = () => {
      switch (errorType) {
        case 'expired':
          return isRTL
            ? 'קישורי סקירה תקפים ל-2 שעות בלבד. העסקאות כבר נשמרו במערכת.'
            : 'Review links are valid for 2 hours only. The transactions have already been saved.';
        case 'not_found':
          return isRTL
            ? 'ייתכן שהקישור שגוי או שכבר נמחק מהמערכת.'
            : 'The link may be incorrect or has been deleted from the system.';
        case 'network':
          return isRTL
            ? 'אנא בדוק את החיבור לאינטרנט ונסה שוב.'
            : 'Please check your internet connection and try again.';
        default:
          return isRTL
            ? 'אנא נסה שוב מאוחר יותר או פנה לתמיכה.'
            : 'Please try again later or contact support.';
      }
    };

    return (
      <div
        className={`min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 ${isRTL ? 'rtl' : 'ltr'}`}
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        <Card className="max-w-md w-full p-6 md:p-8 text-center shadow-xl">
          <div className="flex flex-col items-center space-y-4">
            {/* Error Icon */}
            <div className="animate-pulse">{getErrorIcon()}</div>

            {/* Error Title */}
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-100">
              {getErrorTitle()}
            </h1>

            {/* Error Message */}
            <p className="text-base md:text-lg text-gray-600 dark:text-gray-400">
              {getErrorMessage()}
            </p>

            {/* Help Text */}
            <Alert className={isRTL ? 'text-right' : 'text-left'}>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="text-sm">
                {getErrorHelp()}
              </AlertDescription>
            </Alert>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 w-full mt-6">
              {errorType === 'network' && (
                <Button
                  onClick={() => {
                    setError(null);
                    setLoading(true);
                    fetchReviewSession();
                  }}
                  variant="default"
                  className="flex-1"
                >
                  <AlertCircle className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                  {isRTL ? 'נסה שוב' : 'Try Again'}
                </Button>
              )}
              <Button
                onClick={() => navigate('/transactions')}
                variant={errorType === 'network' ? 'outline' : 'default'}
                className="flex-1"
              >
                <Home className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                {isRTL ? 'לעסקאות שלי' : 'My Transactions'}
              </Button>
            </div>

            {/* Additional Info */}
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-4">
              {isRTL
                ? 'לעזרה נוספת, צור קשר עם התמיכה'
                : 'For additional help, contact support'}
            </p>
          </div>
        </Card>
      </div>
    );
  }

  const expiresAt = new Date(session.expiresAt);
  const now = new Date();
  const timeRemaining = Math.max(0, expiresAt.getTime() - now.getTime());
  const hoursRemaining = Math.floor(timeRemaining / (1000 * 60 * 60));
  const minutesRemaining = Math.floor(
    (timeRemaining % (1000 * 60 * 60)) / (1000 * 60),
  );

  return (
    <div
      className={`container max-w-4xl mx-auto p-3 md:p-4 space-y-4 md:space-y-6 ${isRTL ? 'rtl' : 'ltr'}`}
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {/* Header */}
      <Card className="p-4 md:p-6">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className={`flex-1 ${isRTL ? 'order-2 md:order-1' : ''}`}>
            <h1 className="text-xl md:text-2xl font-bold">
              {isRTL ? 'עדכון עסקאות' : 'Review Transactions'}
            </h1>
            <p className="text-sm md:text-base text-muted-foreground">
              {isRTL
                ? 'בדוק ועדכן את פרטי העסקאות שנוצרו'
                : 'Review and update the created transactions'}
            </p>
          </div>
          <Badge
            variant="outline"
            className={`flex items-center gap-2 self-end md:self-auto ${isRTL ? 'order-1 md:order-2 self-start' : ''}`}
          >
            <Clock className="h-4 w-4" />
            <span className="text-sm">
              {hoursRemaining > 0
                ? `${hoursRemaining}h ${minutesRemaining}m`
                : `${minutesRemaining}m`}
            </span>
          </Badge>
        </div>
      </Card>

      {/* Empty State - No Transactions */}
      {transactions.length === 0 && (
        <Card className="p-8 md:p-12">
          <div className="flex flex-col items-center justify-center space-y-6 text-center">
            {/* Animated Icon */}
            <div className="relative">
              <div className="absolute inset-0 animate-ping opacity-20">
                <Inbox className="h-20 w-20 md:h-24 md:w-24 text-blue-500" />
              </div>
              <Inbox className="h-20 w-20 md:h-24 md:w-24 text-blue-500 animate-bounce" />
            </div>

            {/* Title */}
            <div className="space-y-2">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-100">
                {isRTL ? 'אין עסקאות 😊' : 'No Transactions 😊'}
              </h2>
              <p className="text-base md:text-lg text-gray-600 dark:text-gray-400">
                {isRTL
                  ? 'נראה שמחקת את כל העסקאות!'
                  : 'Looks like you deleted them all!'}
              </p>
            </div>

            {/* Description */}
            <div className="max-w-md">
              <p className="text-sm md:text-base text-muted-foreground">
                {isRTL
                  ? 'כל העסקאות שהיו בקישור זה נמחקו. לא נותרו עסקאות לסקירה.'
                  : 'All transactions from this review link have been deleted. There are no transactions left to review.'}
              </p>
            </div>

            {/* Action Button */}
            <Button
              onClick={() => navigate('/transactions')}
              size="lg"
              className={`mt-4 ${isRTL ? 'flex-row-reverse' : ''}`}
            >
              <Home className={`h-5 w-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
              {isRTL ? 'חזור לעסקאות' : 'Go to Transactions'}
            </Button>
          </div>
        </Card>
      )}

      {/* Transactions List */}
      {transactions.length > 0 && transactions.map((transaction) => (
        <Card key={transaction.id} className="p-3 md:p-6">
          <div className="space-y-2 md:space-y-4">
            {/* Business Name */}
            <div className="flex items-center gap-2">
              <Label
                htmlFor={`business-${transaction.id}`}
                className="text-xs md:text-sm whitespace-nowrap min-w-[60px] md:min-w-[80px]"
              >
                {isRTL ? 'שם העסק' : 'Business name'}
              </Label>
              <Input
                id={`business-${transaction.id}`}
                value={transaction.businessName ?? ''}
                onChange={(e) =>
                  handleTransactionUpdate(
                    transaction.id,
                    'businessName',
                    e.target.value,
                  )
                }
                placeholder={isRTL ? 'שם העסק' : 'Business name'}
                className="text-sm md:text-base"
              />
            </div>

            {/* Description */}
            <div className="flex items-center gap-2">
              <Label 
                htmlFor={`desc-${transaction.id}`} 
                className="text-xs md:text-sm whitespace-nowrap min-w-[60px] md:min-w-[80px]"
              >
                {isRTL ? 'תיאור' : 'Description'}
              </Label>
              <Input
                id={`desc-${transaction.id}`}
                value={transaction.description}
                onChange={(e) =>
                  handleTransactionUpdate(
                    transaction.id,
                    'description',
                    e.target.value,
                  )
                }
                className="text-sm md:text-base"
              />
            </div>

            {/* Amount */}
            <div className="flex items-center gap-2">
              <Label 
                htmlFor={`amount-${transaction.id}`} 
                className="text-xs md:text-sm whitespace-nowrap min-w-[60px] md:min-w-[80px]"
              >
                {isRTL ? 'סכום' : 'Amount'}
              </Label>
              <Input
                id={`amount-${transaction.id}`}
                type="number"
                value={Math.abs(transaction.amount)}
                onChange={(e) =>
                  handleTransactionUpdate(
                    transaction.id,
                    'amount',
                    transaction.amount < 0
                      ? -Math.abs(parseFloat(e.target.value))
                      : Math.abs(parseFloat(e.target.value)),
                  )
                }
                className="text-sm md:text-base"
              />
            </div>

            {/* Date */}
            <div className="flex items-center gap-2">
              <Label 
                htmlFor={`date-${transaction.id}`} 
                className="text-xs md:text-sm whitespace-nowrap min-w-[60px] md:min-w-[80px]"
              >
                {isRTL ? 'תאריך' : 'Date'}
              </Label>
              <Input
                id={`date-${transaction.id}`}
                type="date"
                value={format(new Date(transaction.date), 'yyyy-MM-dd')}
                onChange={(e) =>
                  handleTransactionUpdate(
                    transaction.id,
                    'date',
                    e.target.value,
                  )
                }
                className="text-sm md:text-base"
              />
            </div>

            {/* Category */}
            <div className="flex items-center gap-2">
              <Label 
                htmlFor={`category-${transaction.id}`} 
                className="text-xs md:text-sm whitespace-nowrap min-w-[60px] md:min-w-[80px]"
              >
                {isRTL ? 'קטגוריה' : 'Category'}
              </Label>
              <div className="flex-1 flex gap-2">
                <Select
                  value={transaction.categoryId}
                  onValueChange={(value) =>
                    handleTransactionUpdate(transaction.id, 'categoryId', value)
                  }
                >
                  <SelectTrigger className="text-sm md:text-base">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {session.categories.map((category) => (
                      <SelectItem key={category.id} value={category.id}>
                        <div className="flex items-center gap-2">
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: category.color }}
                          />
                          <span>{category.name}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => {
                    setCategoryForTransactionId(transaction.id);
                    setCreateCategoryOpen(true);
                  }}
                  title={isRTL ? 'צור קטגוריה חדשה' : 'Create new category'}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Delete Button */}
            <div className="flex pt-2 border-t justify-end">
              <Button
                variant="destructive"
                size="sm"
                onClick={() => handleDeleteTransaction(transaction.id)}
                disabled={deletingIds.has(transaction.id)}
                className="text-xs md:text-sm"
              >
                <Trash2 className={`h-3 w-3 md:h-4 md:w-4 ${isRTL ? 'ml-1.5' : 'mr-1.5'}`} />
                {deletingIds.has(transaction.id) 
                  ? (isRTL ? 'מוחק...' : 'Deleting...')
                  : (isRTL ? 'מחק עסקה' : 'Delete Transaction')
                }
              </Button>
            </div>
          </div>
        </Card>
      ))}

      {/* Save Button */}
      {hasChanges && (
        <Card className="p-3 md:p-4 sticky bottom-2 md:bottom-4 shadow-lg">
          <Button
            onClick={handleSaveAll}
            disabled={saving}
            className="w-full text-sm md:text-base"
            size="lg"
          >
            <Save className={`h-5 w-5 ${isRTL ? 'ml-2' : 'mr-2'} ${saving ? 'animate-spin' : ''}`} />
            {saving 
              ? (isRTL ? 'שומר...' : 'Saving...')
              : (isRTL ? 'שמור שינויים' : 'Save Changes')
            }
          </Button>
        </Card>
      )}

      {/* Success State — only after the user actually saved changes */}
      {hasSaved && !hasChanges && transactions.length > 0 && (
        <Card className="p-4 md:p-6 bg-gradient-to-br from-green-50 to-green-100 dark:from-green-950 dark:to-green-900 border-green-200 dark:border-green-800">
          <div className="flex flex-col items-center gap-4 text-center">
            <div
              className={`flex items-center gap-2 text-green-700 dark:text-green-300 text-base md:text-lg font-semibold ${isRTL ? 'flex-row-reverse' : ''}`}
            >
              <CheckCircle className="h-6 w-6" />
              <span>
                {isRTL ? 'כל השינויים נשמרו בהצלחה!' : 'All changes saved successfully!'}
              </span>
            </div>
            <p className="text-sm text-green-600 dark:text-green-400">
              {isRTL
                ? 'העסקאות עודכנו במערכת ומוצגות ברשימת העסקאות שלך'
                : 'Transactions have been updated and are now visible in your transaction list'}
            </p>
            <Button
              onClick={() => navigate('/transactions')}
              variant="outline"
              className="mt-2 border-green-300 hover:bg-green-100 dark:border-green-700 dark:hover:bg-green-900"
            >
              <Home className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
              {isRTL ? 'לעסקאות שלי' : 'View Transactions'}
            </Button>
          </div>
        </Card>
      )}

      {/* Create Category Dialog */}
      <Dialog open={createCategoryOpen} onOpenChange={setCreateCategoryOpen}>
        <DialogContent className={isRTL ? 'rtl' : 'ltr'} dir={isRTL ? 'rtl' : 'ltr'}>
          <DialogHeader>
            <DialogTitle className={isRTL ? 'text-right' : 'text-left'}>
              {isRTL ? 'צור קטגוריה חדשה' : 'Create New Category'}
            </DialogTitle>
            <DialogDescription className={isRTL ? 'text-right' : 'text-left'}>
              {isRTL
                ? 'הוסף קטגוריה חדשה לחשבון שלך'
                : 'Add a new category to your account'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Category Name */}
            <div className="space-y-2">
              <Label htmlFor="category-name" className={isRTL ? 'text-right' : 'text-left'}>
                {isRTL ? 'שם הקטגוריה' : 'Category Name'}
              </Label>
              <Input
                id="category-name"
                value={categoryForm.name}
                onChange={(e) =>
                  setCategoryForm({ ...categoryForm, name: e.target.value })
                }
                placeholder={isRTL ? 'לדוגמה: מזון' : 'e.g., Food'}
                className={isRTL ? 'text-right' : 'text-left'}
              />
            </div>

            {/* Category Type */}
            <div className="space-y-2">
              <Label htmlFor="category-type" className={isRTL ? 'text-right' : 'text-left'}>
                {isRTL ? 'סוג' : 'Type'}
              </Label>
              <Select
                value={categoryForm.type}
                onValueChange={(value: 'expense' | 'income') =>
                  setCategoryForm({ ...categoryForm, type: value })
                }
              >
                <SelectTrigger className={isRTL ? 'text-right' : 'text-left'}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="expense">
                    {isRTL ? 'הוצאה' : 'Expense'}
                  </SelectItem>
                  <SelectItem value="income">
                    {isRTL ? 'הכנסה' : 'Income'}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Color Picker */}
            <div className="space-y-2">
              <Label className={isRTL ? 'text-right' : 'text-left'}>
                {isRTL ? 'צבע' : 'Color'}
              </Label>
              <div className="grid grid-cols-10 gap-2">
                {colorOptions.map((color) => (
                  <button
                    key={color}
                    type="button"
                    className={`w-8 h-8 rounded-full border-2 transition-all ${
                      categoryForm.color === color
                        ? 'border-primary ring-2 ring-primary ring-offset-2'
                        : 'border-transparent hover:border-gray-300'
                    }`}
                    style={{ backgroundColor: color }}
                    onClick={() =>
                      setCategoryForm({ ...categoryForm, color })
                    }
                    title={color}
                  />
                ))}
              </div>
            </div>
          </div>

          <DialogFooter className={isRTL ? 'flex-row-reverse' : ''}>
            <Button
              variant="outline"
              onClick={() => setCreateCategoryOpen(false)}
              disabled={creatingCategory}
            >
              {isRTL ? 'ביטול' : 'Cancel'}
            </Button>
            <Button
              onClick={handleCreateCategory}
              disabled={creatingCategory || !categoryForm.name.trim()}
            >
              {creatingCategory ? (
                <>
                  <Clock className={`h-4 w-4 animate-spin ${isRTL ? 'ml-2' : 'mr-2'}`} />
                  {isRTL ? 'יוצר...' : 'Creating...'}
                </>
              ) : (
                <>
                  <Plus className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                  {isRTL ? 'צור קטגוריה' : 'Create Category'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

